import { Input, Select, SelectItem, Switch, Textarea } from '@nextui-org/react';
import type { Credential } from '@circuitry-ai/doc-data';
import {
  BUSINESS_TOOL_AUTH_TYPES,
  BUSINESS_TOOL_HTTP_METHODS,
  type AuthFormState,
  type BusinessToolFormState,
} from './businessToolFormUtils';
import { KeyValueEditor, SectionHeading } from './businessToolFormControls';

export type BusinessToolApiAndResponseSectionsProps = Readonly<{
  form: BusinessToolFormState;
  credentialOptions: Credential[];
  isCredentialsLoading: boolean;
  onFormChange: (patch: Partial<BusinessToolFormState>) => void;
  onUpdateAuth: (patch: Partial<AuthFormState>) => void;
}>;

function AuthenticationFields({
  authentication,
  credentialOptions,
  isCredentialsLoading,
  onUpdateAuth,
}: Readonly<{
  authentication: AuthFormState;
  credentialOptions: Credential[];
  isCredentialsLoading: boolean;
  onUpdateAuth: (patch: Partial<AuthFormState>) => void;
}>) {
  if (authentication.type === 'bearer') {
    return (
      <Input
        label="Bearer Token"
        labelPlacement="outside"
        type="password"
        variant="bordered"
        value={authentication.token}
        onValueChange={(value) => onUpdateAuth({ token: value })}
      />
    );
  }

  if (authentication.type === 'api_key') {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input
          label="API Key"
          labelPlacement="outside"
          type="password"
          variant="bordered"
          value={authentication.api_key}
          onValueChange={(value) => onUpdateAuth({ api_key: value })}
        />
        <Input
          label="Header name"
          labelPlacement="outside"
          variant="bordered"
          value={authentication.header_name}
          onValueChange={(value) => onUpdateAuth({ header_name: value })}
        />
      </div>
    );
  }

  if (authentication.type === 'basic') {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input
          label="Username"
          labelPlacement="outside"
          variant="bordered"
          value={authentication.username}
          onValueChange={(value) => onUpdateAuth({ username: value })}
        />
        <Input
          label="Password"
          labelPlacement="outside"
          type="password"
          variant="bordered"
          value={authentication.password}
          onValueChange={(value) => onUpdateAuth({ password: value })}
        />
      </div>
    );
  }

  if (authentication.type === 'oauth2') {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Input
          label="Client ID"
          labelPlacement="outside"
          variant="bordered"
          value={authentication.client_id}
          onValueChange={(value) => onUpdateAuth({ client_id: value })}
        />
        <Input
          label="Client Secret"
          labelPlacement="outside"
          type="password"
          variant="bordered"
          value={authentication.client_secret}
          onValueChange={(value) => onUpdateAuth({ client_secret: value })}
        />
        <Input
          label="Token URL"
          labelPlacement="outside"
          variant="bordered"
          value={authentication.token_url}
          onValueChange={(value) => onUpdateAuth({ token_url: value })}
        />
        <Input
          label="Scope"
          labelPlacement="outside"
          variant="bordered"
          value={authentication.scope}
          onValueChange={(value) => onUpdateAuth({ scope: value })}
        />
        <Select
          label="Grant type"
          labelPlacement="outside"
          variant="bordered"
          selectedKeys={[authentication.grant_type]}
          onSelectionChange={(keys) => {
            const value = Array.from(keys)[0] as AuthFormState['grant_type'] | undefined;
            if (value) onUpdateAuth({ grant_type: value });
          }}
        >
          <SelectItem key="client_credentials">client_credentials</SelectItem>
          <SelectItem key="authorization_code">authorization_code</SelectItem>
        </Select>
      </div>
    );
  }

  if (authentication.type === 'custom') {
    return (
      <KeyValueEditor
        pairs={authentication.headers}
        onChange={(headers) => onUpdateAuth({ headers })}
        keyLabel="Header"
        valueLabel="Value"
        addLabel="Add auth header"
      />
    );
  }

  if (authentication.type === 'custom_token_db') {
    return (
      <Select
        label="Credential"
        labelPlacement="outside"
        variant="bordered"
        isLoading={isCredentialsLoading}
        selectedKeys={authentication.credential_id ? [authentication.credential_id] : []}
        onSelectionChange={(keys) => {
          const value = Array.from(keys)[0];
          onUpdateAuth({ credential_id: value ? String(value) : '' });
        }}
        placeholder="Select stored credential"
      >
        {credentialOptions.map((credential) => (
          <SelectItem key={credential.credential_id} textValue={credential.name}>
            {credential.name}
          </SelectItem>
        ))}
      </Select>
    );
  }

  return null;
}

export function BusinessToolApiAndResponseSections({
  form,
  credentialOptions,
  isCredentialsLoading,
  onFormChange,
  onUpdateAuth,
}: BusinessToolApiAndResponseSectionsProps) {
  return (
    <>
      <div className="flex flex-col gap-3">
        <SectionHeading title="API Configuration" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input
            label="Base URL"
            labelPlacement="outside"
            isRequired
            variant="bordered"
            placeholder="https://api.example.com"
            value={form.base_url}
            onValueChange={(value) => onFormChange({ base_url: value })}
          />
          <Input
            label="Endpoint"
            labelPlacement="outside"
            isRequired
            variant="bordered"
            placeholder="/v1/resource"
            value={form.endpoint}
            onValueChange={(value) => onFormChange({ endpoint: value })}
          />
          <Select
            label="Method"
            labelPlacement="outside"
            variant="bordered"
            selectedKeys={[form.method]}
            onSelectionChange={(keys) => {
              const value = Array.from(keys)[0] as BusinessToolFormState['method'] | undefined;
              if (value) onFormChange({ method: value });
            }}
          >
            {BUSINESS_TOOL_HTTP_METHODS.map((method) => (
              <SelectItem key={method}>{method}</SelectItem>
            ))}
          </Select>
          <Input
            type="number"
            label="Timeout (seconds)"
            labelPlacement="outside"
            variant="bordered"
            min={1}
            max={300}
            value={String(form.timeout_seconds)}
            onValueChange={(value) =>
              onFormChange({
                timeout_seconds: Math.min(300, Math.max(1, Number(value) || 1)),
              })
            }
            description="Must exceed your slowest expected API/workflow run."
          />
        </div>

        <div>
          <p className="text-xs text-default-500 mb-2">Query params</p>
          <KeyValueEditor
            pairs={form.query_params}
            onChange={(query_params) => onFormChange({ query_params })}
            keyLabel="Key"
            valueLabel="Value"
            addLabel="Add query param"
          />
        </div>

        <Textarea
          label="Body Template (JSON)"
          labelPlacement="outside"
          variant="bordered"
          minRows={6}
          value={form.body_template}
          onValueChange={(value) => onFormChange({ body_template: value })}
          placeholder={`{\n  "payload": {\n    "session_id": "{{pre_request.session_id}}"\n  }\n}`}
          classNames={{ input: 'font-mono text-sm' }}
          description="Supports {{param}}, {{pre_request.x}}, {{auth_token}}"
        />

        <div className="flex flex-col gap-3 rounded-lg border border-divider p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-default-500">
            Authentication
          </p>
          <Select
            label="Auth type"
            labelPlacement="outside"
            variant="bordered"
            selectedKeys={[form.authentication.type]}
            onSelectionChange={(keys) => {
              const value = Array.from(keys)[0] as AuthFormState['type'] | undefined;
              if (value) onUpdateAuth({ type: value });
            }}
          >
            {BUSINESS_TOOL_AUTH_TYPES.map((type) => (
              <SelectItem key={type}>{type}</SelectItem>
            ))}
          </Select>
          <AuthenticationFields
            authentication={form.authentication}
            credentialOptions={credentialOptions}
            isCredentialsLoading={isCredentialsLoading}
            onUpdateAuth={onUpdateAuth}
          />
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <SectionHeading title="Response Handling" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Select
            label="Response Format"
            labelPlacement="outside"
            variant="bordered"
            selectedKeys={[form.response_body_format]}
            onSelectionChange={(keys) => {
              const value = Array.from(keys)[0] as
                | BusinessToolFormState['response_body_format']
                | undefined;
              if (value) onFormChange({ response_body_format: value });
            }}
          >
            <SelectItem key="json">JSON</SelectItem>
            <SelectItem key="sse">SSE (Server-Sent Events)</SelectItem>
          </Select>
          {form.response_body_format === 'sse' ? (
            <Input
              label="SSE final event name"
              labelPlacement="outside"
              variant="bordered"
              value={form.sse_final_event_name}
              onValueChange={(value) => onFormChange({ sse_final_event_name: value })}
            />
          ) : (
            <div />
          )}
          <Input
            label="Response Extract Path"
            labelPlacement="outside"
            variant="bordered"
            placeholder="data.outputs.text"
            value={form.response_extract_path}
            onValueChange={(value) => onFormChange({ response_extract_path: value })}
            description="Dot-path to the part of the response the AI should receive. Without it the AI gets the ENTIRE response."
            className="sm:col-span-2"
          />
          <div className="flex items-center">
            <Switch
              size="sm"
              isSelected={form.response_parse_inner_json}
              onValueChange={(value) => onFormChange({ response_parse_inner_json: value })}
            >
              Parse inner JSON
            </Switch>
          </div>
          <Input
            type="number"
            label="Response max chars"
            labelPlacement="outside"
            variant="bordered"
            placeholder="12000"
            value={form.response_max_chars}
            onValueChange={(value) => onFormChange({ response_max_chars: value })}
          />
          <Input
            label="Response field path"
            labelPlacement="outside"
            variant="bordered"
            value={form.response_field_path}
            isDisabled={!form.response_extract_path.trim()}
            onValueChange={(value) => onFormChange({ response_field_path: value })}
            description="Only relevant when extract path is set."
          />
          <p className="text-xs text-default-400 self-center">
            Only enable Parse inner JSON if the extracted value is a JSON string that needs parsing.
          </p>
          <Textarea
            label="Success Message"
            labelPlacement="outside"
            variant="bordered"
            minRows={2}
            placeholder="Request completed successfully"
            value={form.success_message}
            onValueChange={(value) => onFormChange({ success_message: value })}
            description="Supports {{response}} + response fields"
          />
          <Textarea
            label="Error Message"
            labelPlacement="outside"
            variant="bordered"
            minRows={2}
            placeholder="Something went wrong, please try again"
            value={form.error_message}
            onValueChange={(value) => onFormChange({ error_message: value })}
            description="The AI uses this to explain the failure to the caller."
          />
        </div>
      </div>
    </>
  );
}
